<template>
  <v-card flat>
    <v-toolbar flat>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSubmit">
        <v-text-field
          class="mr-2"
          v-model="search"
          :label="$i18n.translate('Filter failed rows')"
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          @click:clear="onClear"
        ></v-text-field>
      </v-form>
    </v-toolbar>
    <v-card-text>
      <ApiError :errors="errors"></ApiError>
      <v-data-table
        :headers="computedHeaders"
        :items="computedItems"
        :server-items-length="total"
        :options.sync="options"
        :loading="loading"
        class="elevation-1"
        dense
      >
        <template v-slot:item.rowNumber="{ item }">
          <span class="font-weight-bold">#{{ item.rowNumber }}</span>
        </template>
        <template v-slot:item.errorMessages="{ item }">
          <span class="red--text text--darken-3 font-weight-medium">{{ item.errorMessages }}</span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import BulkPayoutBatchService from "@/gapp-components/services/bulkPayoutBatch.service";
import BulkPayoutBatchRowErrorService from "@/gapp-components/services/bulkPayoutBatchRowError.service";
import ApiError from "@/gapp-components/components/display/ApiError.vue";

export default {
  name: "BulkPayoutBatch",
  components: {
    ApiError
  },
  props: {
    batchId: [Number, String]
  },
  data() {
    return {
      search: "",
      errors: {},
      items: [],
      savedHeaders: [],
      options: {
        itemsPerPage: 10,
        page: 1
      },
      total: 0,
      loading: false
    };
  },
  computed: {
    sortedSavedHeaders() {
      return [...this.savedHeaders].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    },
    headerLabelByKey() {
      const map = {};
      this.sortedSavedHeaders.forEach(header => {
        if (header.key) map[header.key] = header.label;
      });
      return map;
    },
    dynamicKeys() {
      const keys = new Set();
      this.items.forEach(item => {
        const values = this.parseValues(item.rowValues);
        Object.keys(values).forEach(k => keys.add(k));
      });
      return Array.from(keys);
    },
    computedHeaders() {
      const headers = [{ text: "Row #", value: "rowNumber", width: "90px", sortable: false, align: "center" }];

      this.dynamicKeys.forEach((key, index) => {
        headers.push({
          text: this.headerLabelByKey[key] || key,
          value: `col_${index}`,
          sortable: false
        });
      });

      headers.push({
        text: "Error Details",
        value: "errorMessages",
        sortable: false
      });

      return headers;
    },
    computedItems() {
      return this.items.map(item => {
        const values = this.parseValues(item.rowValues);
        const row = { ...item };
        this.dynamicKeys.forEach((key, index) => {
          row[`col_${index}`] = values[key] !== undefined && values[key] !== null ? values[key] : "-";
        });
        return row;
      });
    }
  },
  created() {
    this.getHeaders();
  },
  watch: {
    batchId() {
      this.search = "";
      this.savedHeaders = [];
      this.options.page = 1;
      this.getHeaders();
      this.getErrors();
    },
    options: {
      handler: "getErrors",
      deep: true
    }
  },
  methods: {
    onSubmit() {
      this.options.page = 1;
      this.getErrors();
    },
    onClear() {
      this.search = "";
      this.options.page = 1;
      this.getErrors();
    },
    async getHeaders() {
      if (!this.batchId) return;
      try {
        const { data: response } = await BulkPayoutBatchService.getHeaders(this.batchId);
        this.savedHeaders = this.normalizeHeaders(response);
      } catch (error) {
        this.errors = this.$api.getErrorsFromResponse(error);
      }
    },
    normalizeHeaders(response) {
      if (Array.isArray(response)) return response;
      if (Array.isArray(response?.content)) return response.content;
      if (Array.isArray(response?.headers)) return response.headers;
      return [];
    },
    async getErrors() {
      if (!this.batchId) return;

      this.items = [];
      this.total = 0;
      this.errors = {};
      this.loading = true;

      try {
        const filters = {
          bulkPayoutBatch: { id: this.batchId }
        };
        if (this.search && this.search.trim().length > 0) {
          filters.keyword = this.search.trim();
        }
        const { data: response } = await BulkPayoutBatchRowErrorService.search(filters, this.options);
        this.items = response.content || [];
        this.total = response.totalElements || 0;
      } catch (error) {
        this.errors = this.$api.getErrorsFromResponse(error);
      } finally {
        this.loading = false;
      }
    },
    parseValues(jsonStr) {
      if (!jsonStr) return {};
      try {
        return JSON.parse(jsonStr);
      } catch (e) {
        return { value: jsonStr };
      }
    }
  }
};
</script>
